// Copyright (c) Ping. All rights reserved.

// Pure validators — discriminants match the Wave-3 cross-platform
// validation contract. The failure-reason set is shared with iOS-AUTH
// and AND-AUTH (see `_auth_validator_fixtures.json`); if you add a
// new failure reason here, add the matching case on iOS / Android in
// the same PR.
//
// Validators MUST stay pure: no IO, no logging, no clock, no random.
//
// invite-flow Wave-3B: phone validation now uses `libphonenumber-js`
// (E.164 parser). Web's behaviour is preserved verbatim; desktop /
// iOS / Android get the stricter check for free. The result shape is
// `{ ok: true; value: T }` on success so callers can use the
// normalized (lowercased / E.164 / trimmed) form.

import { ParseError, type PhoneNumber, parsePhoneNumber } from 'libphonenumber-js';

export type EmailFailure = 'empty' | 'invalidFormat';
export type PhoneFailure =
  | 'empty'
  | 'invalidCountryCode'
  | 'tooShort'
  | 'tooLong'
  | 'invalidFormat';
export type CodeFailure = 'empty' | 'wrongLength' | 'nonNumeric';
export type PasswordFailure =
  | 'empty'
  | 'tooShort'
  | 'tooLong'
  | 'tooSimple'
  | 'mustContainNumber'
  | 'mustContainSymbol';
export type DisplayNameFailure =
  | 'empty'
  | 'tooShort'
  | 'tooLong'
  | 'invalidCharacters';

export type ValidationResult<T, F> = { ok: true; value: T } | { ok: false; reason: F };

const ok = <T, F>(value: T): ValidationResult<T, F> => ({ ok: true, value });
const fail = <T, F>(reason: F): ValidationResult<T, F> => ({ ok: false, reason });

// Practical bounds matching iOS/Android contract — see
// the cross-platform `_auth_validator_fixtures.json`.
const PASSWORD_MIN_LEN = 8;
const PASSWORD_MAX_LEN = 256;
const DISPLAY_NAME_MIN_LEN = 2;
const DISPLAY_NAME_MAX_LEN = 32;
const CODE_LEN = 6;
// libphonenumber default region for bare-national-number inputs. The
// fixture pins `_default_region: "US"`; iOS / Android pass the same
// default to PhoneNumberKit / libphonenumber respectively.
const DEFAULT_PHONE_REGION = 'US' as const;

// RFC-5322-lite. Refusing pre-filtered obvious cases is enough; the
// server is the authority on email validity.
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const SYMBOL_RE = /[^A-Za-z0-9]/;
const DIGIT_RE = /[0-9]/;

// Detect ASCII control codes (0x00-0x1F + 0x7F) anywhere in a display
// name. Emoji, accents, CJK, Arabic etc are all fine.
function containsControlCharacter(input: string): boolean {
  for (let i = 0; i < input.length; i += 1) {
    const code = input.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

export function validateEmail(input: string): ValidationResult<string, EmailFailure> {
  const trimmed = input.trim();
  if (trimmed.length === 0) return fail('empty');
  if (!EMAIL_RE.test(trimmed)) return fail('invalidFormat');
  return ok(trimmed.toLowerCase());
}

// Allowed phone-input characters. libphonenumber strips non-digits
// before parsing, so "+15-abc-def" would parse to "+15" and surface as
// TOO_SHORT — semantically wrong. Rejecting upfront on invalid chars
// gives the correct discriminant (`invalidFormat`).
const PHONE_ALLOWED_CHAR_RE = /^[+0-9\s().\-]+$/;

export function validatePhone(input: string): ValidationResult<string, PhoneFailure> {
  const trimmed = input.trim();
  if (trimmed.length === 0) return fail('empty');
  if (!PHONE_ALLOWED_CHAR_RE.test(trimmed)) return fail('invalidFormat');
  // Reject doubled `+` ("++15551234567") before libphonenumber would
  // strip it and accept the residue.
  if (/\+{2,}/.test(trimmed)) return fail('invalidFormat');

  let parsed: PhoneNumber | undefined;
  try {
    parsed = parsePhoneNumber(trimmed, DEFAULT_PHONE_REGION);
  } catch (err) {
    if (err instanceof ParseError) {
      switch (err.message) {
        case 'TOO_SHORT':
          return fail('tooShort');
        case 'TOO_LONG':
          return fail('tooLong');
        case 'INVALID_COUNTRY':
          return fail('invalidCountryCode');
        default:
          // Includes NOT_A_NUMBER and any future libphonenumber error
          // discriminant we have not yet enumerated.
          return fail('invalidFormat');
      }
    }
    return fail('invalidFormat');
  }

  if (!parsed) return fail('invalidFormat');
  if (!parsed.countryCallingCode) return fail('invalidCountryCode');

  const national = parsed.nationalNumber.toString();
  // Bound by the E.164 length range (national subscriber number is at
  // least 7 digits, country + national together no more than 15). The
  // client validator does NOT call `.isValid()` — that rejects reserved
  // ranges (e.g. NANP 555-prefix fictional numbers) which the user is
  // entitled to type at sign-up; the server is the final authority on
  // dialability via the SMS-request endpoint's response.
  if (national.length < 7) return fail('tooShort');
  if (national.length > 15) return fail('tooLong');

  return ok(parsed.number);
}

export function validateCode(input: string): ValidationResult<string, CodeFailure> {
  const trimmed = input.trim();
  if (trimmed.length === 0) return fail('empty');
  if (!/^\d+$/.test(trimmed)) return fail('nonNumeric');
  if (trimmed.length !== CODE_LEN) return fail('wrongLength');
  return ok(trimmed);
}

export function validatePassword(input: string): ValidationResult<string, PasswordFailure> {
  if (input.length === 0) return fail('empty');
  if (input.length < PASSWORD_MIN_LEN) return fail('tooShort');
  if (input.length > PASSWORD_MAX_LEN) return fail('tooLong');
  if (isPasswordTooSimple(input)) return fail('tooSimple');
  if (!DIGIT_RE.test(input)) return fail('mustContainNumber');
  if (!SYMBOL_RE.test(input)) return fail('mustContainSymbol');
  return ok(input);
}

export function validateDisplayName(input: string): ValidationResult<string, DisplayNameFailure> {
  const trimmed = input.trim();
  if (trimmed.length === 0) return fail('empty');
  if (containsControlCharacter(trimmed)) return fail('invalidCharacters');
  if (countCodepoints(trimmed) < DISPLAY_NAME_MIN_LEN) return fail('tooShort');
  if (countCodepoints(trimmed) > DISPLAY_NAME_MAX_LEN) return fail('tooLong');
  return ok(trimmed);
}

// "Too simple" catches passwords that pass the length / digit / symbol
// gates but contain a low-entropy run anywhere inside them. The shared
// fixture file pins the threshold at 3 consecutive characters that are
// either identical or strict +1 ASCII ascending / -1 descending. iOS-AUTH
// and AND-AUTH ship the same rule; if you change the threshold, change
// all three platforms in one PR.
function isPasswordTooSimple(input: string): boolean {
  if (input.length < 3) return false;
  for (let i = 0; i + 2 < input.length; i += 1) {
    const a = input.charCodeAt(i);
    const b = input.charCodeAt(i + 1);
    const c = input.charCodeAt(i + 2);
    if (a === b && b === c) return true;
    if (b - a === 1 && c - b === 1) return true;
    if (a - b === 1 && b - c === 1) return true;
  }
  return false;
}

// JS string .length counts UTF-16 code units; emoji surrogate pairs
// inflate the count. Display-name length is measured in code points to
// stay consistent with iOS (Swift's `String.count`) and Android
// (Kotlin's `String.codePointCount`).
function countCodepoints(input: string): number {
  let count = 0;
  for (const _ of input) {
    count += 1;
  }
  return count;
}
